import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import { handleGetItemsWithParam } from "../../shared/controllers/blogController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import QRScanner from "../../utils/QRScanner";
import landingAsset from "../utils/assetPaths";

import left from "./assets/left.svg";
import right from "./assets/right.svg";

interface Slide {
    title: string;
    excerpt: string;
    image: string;
}

const Hero: React.FC = () => {
    const [isScannerOpen, setScannerOpen] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [scanResult, setScanResult] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const swiperRef = useRef<unknown>(null);

    const goToNextSlide = () => {
        if (swiperRef.current) (swiperRef.current as { slideNext: () => void }).slideNext();
    };

    const goToPrevSlide = () => {
        if (swiperRef.current) (swiperRef.current as { slidePrev: () => void }).slidePrev();
    };

    const heroBackground = landingAsset("herobg.svg");

    const fallbackSlider: Slide[] = [
        {
            title: "Seamless Pitch Booking at Your Fingertips",
            excerpt:
                "Find and book football pitches in your area effortlessly. With SACA, you can secure a pitch for your team anytime, anywhere, ensuring you always have a place to play.",
            image: heroBackground,
        },
        {
            title: "Compete in Local Matches and Leagues",
            excerpt:
                "Join or organize competitive matches and leagues right from our website. Whether you're a casual player or aiming to improve your skills, SACA connects you with a wide range of local competitions.",
            image: heroBackground,
        },
        {
            title: "Stay Updated on League Action",
            excerpt:
                "Get live updates on upcoming fixtures, match results, and league standings. Track your favorite teams and leagues to stay on top of all the latest football action.",
            image: heroBackground,
        },
    ];

    const [slides, setSlides] = useState<Slide[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchSlides = async () => {
        const params = { type: "Slider" };
        try {
            const data = await handleGetItemsWithParam<Slide[]>(params);
            const slidesPayload = unwrapApiArray<Slide>(data);
            if (slidesPayload.length > 0) {
                setSlides(slidesPayload);
            } else {
                setSlides(fallbackSlider);
            }
        } catch (error) {
            console.error("Error fetching slides:", error);
            setSlides(fallbackSlider);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSlides();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const openScanner = () => {
        setScannerOpen(true);
    };

    const closeScanner = () => {
        setScannerOpen(false);
    };

    const handleScan = (data: unknown) => {
        if (data && typeof data === 'string') {
            setScanResult(data);
            closeScanner();
        }
    };

    const currentSlides = loading ? fallbackSlider : slides;

    return (
        <>
            {/* QR Scanner Modal */}
            {isScannerOpen && (
                <QRScanner closeScanner={closeScanner} onScan={handleScan} />
            )}
            <Swiper

                onInit={(swiper: unknown) => {
                    swiperRef.current = swiper;
                    setActiveIndex((swiper as { activeIndex: number }).activeIndex);
                }}

                onSlideChange={(swiper: unknown) => setActiveIndex((swiper as { activeIndex: number }).activeIndex)}
                pagination={{
                    clickable: true,
                    el: ".custom-pagination",
                }}
                modules={[Pagination]}
                className="w-full"
            >
                {currentSlides.map((item, index) => (
                    <SwiperSlide key={index} className=" w-full">
                        <div
                            style={{
                                backgroundImage: `url(${item.image})`,
                                backgroundPosition: "center",
                                backgroundSize: "cover",
                            }}
                            className=" h-svh relative w-full flex justify-center items-center flex-col"
                        >
                            <div
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                                }}
                                className=" h-full w-full absolute left-0 top-0"
                            >
                                <div className=" w-full h-full mt-12 flex flex-col justify-center items-start px-4 md:px-14">
                                    <h1 className=" block md:w-[624px] text-white">
                                        {item.title}
                                    </h1>
                                    <p className=" md:max-w-[500px] xl:max-w-[630px] w-full font-Manrope font-medium text-base md:text-lg text-white mt-6">
                                        {item.excerpt}
                                    </p>
                                    <div className=" flex items-center space-x-3">
                                        <Link
                                            to="/sign-up"
                                            className=" mt-8 py-3 px-[18px] bg-white rounded-[6px] font-Manrope text-[#317333] font-semibold text-[15px]"
                                        >
                                            Join SACA Today
                                        </Link>
                                    </div>
                                    <div className=" mt-20 space-x-8 items-center flex">
                                        <button
                                            className="flex items-center justify-center border-2 border-white rounded-full w-[34px] h-[34px]"
                                            onClick={goToPrevSlide}
                                        >
                                            <img src={left} alt="Previous" />
                                        </button>
                                        <div className="bg-[#E9EDE9] h-[34px] w-[94px] rounded-[30px] flex items-center justify-center space-x-[10px]">
                                            {currentSlides.map((_, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`w-[10px] h-[10px] rounded-full ${idx === activeIndex
                                                        ? "bg-[#317333]"
                                                        : "bg-[#3173334D]"
                                                        }`}
                                                ></span>
                                            ))}
                                        </div>
                                        <button
                                            className="flex items-center justify-center border-2 border-white rounded-full w-[34px] h-[34px]"
                                            onClick={goToNextSlide}
                                        >
                                            <img src={right} alt="Next" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper >
        </>
    );
};

export default Hero;
